// frontend/src/index.js
import React from "react";
import Root from "./components/root";
import configureStore from "./store/store";
import { jwtDecode } from "jwt-decode";
import { createRoot } from "react-dom/client";
import axios from "axios";

import { emitSessionExpired } from "./util/auth_events";
import { setAuthToken } from "./util/session_api_util";
import { logout, hydrateSessionFromDashboard } from "./actions/session_actions";

document.addEventListener("DOMContentLoaded", () => {
  let store;
  try {
    const isStandalone =
      window.matchMedia?.("(display-mode: standalone)")?.matches ||
      window.navigator?.standalone === true;
    if (isStandalone) document.body.classList.add("pwa-standalone");
  } catch (e) {}

  const applyViewportHeightFix = () => {
    const vh = window.innerHeight || document.documentElement.clientHeight || 0;
    const sh = window.screen?.height || 0;
    const h = document.body.classList.contains("pwa-standalone")
      ? (sh || vh)
      : vh;
    if (!h) return;
    document.documentElement.style.setProperty("--app-height", `${h}px`);
    const root = document.getElementById("root");
    if (root) root.style.minHeight = `${h}px`;
    if (document.body.classList.contains("pwa-standalone") && root) {
      root.style.height = `${h}px`;
    }
  };

  applyViewportHeightFix();
  window.addEventListener("resize", applyViewportHeightFix);
  window.addEventListener("orientationchange", applyViewportHeightFix);

  const handleSessionExpired = (reason = "expired") => {
    // save where they were so login can return them
    try {
      const returnTo = window.location.hash || "#/dashboard";
      sessionStorage.setItem("returnTo", returnTo);
    } catch (e) {}

    // clear local token + headers + redux session
    try {
      localStorage.removeItem("jwtToken");
    } catch (e) {}

    setAuthToken(false);

    try {
      if (store) store.dispatch(logout());
    } catch (e) {}

    // show modal (Root listens for this)
    emitSessionExpired(reason);
  };

  const stored = localStorage.getItem("jwtToken");

  // ✅ Support either raw JWT or "Bearer <jwt>"
  const jwt = stored ? stored.replace(/^Bearer\s+/i, "") : null;

  if (jwt) {
    // ✅ Always set axios auth header as Bearer <jwt>
    setAuthToken(`Bearer ${jwt}`);

    let decodedUser = null;

    try {
      decodedUser = jwtDecode(jwt);
    } catch (e) {
      // Bad token: hard redirect is OK here (can’t trust anything)
      store = configureStore({});
      localStorage.removeItem("jwtToken");
      setAuthToken(false);
      window.location.href = "/#/login?expired=1";
      return;
    }

    // ✅ exp is in seconds
    const currentTime = Date.now() / 1000;
    const exp = decodedUser?.exp;

    // If no exp or expired, force logout (hard redirect on load is OK)
    if (!exp || exp < currentTime) {
      store = configureStore({});
      try {
        store.dispatch(logout());
      } finally {
        window.location.href = "/#/login?expired=1";
      }
      return;
    }

    // ✅ Token valid: bootstrap redux session so ProtectedRoute works after refresh
    const preloadedState = {
      session: { isAuthenticated: true, user: decodedUser },
    };

    store = configureStore(preloadedState);
    window.__COSMETINK_STORE__ = store;

    // 💧 Hydrate: fetch full user from /api/users/dashboard
    // If /dashboard returns 401, thunk will clear token + dispatch logout + emit modal.
    store.dispatch(hydrateSessionFromDashboard());

    // ✅ Auto-logout softly when token expires (modal + return-to)
    const msUntilExpiry = Math.max(0, (exp - currentTime) * 1000);

    if (window.__COSMETINK_LOGOUT_TIMER__) clearTimeout(window.__COSMETINK_LOGOUT_TIMER__);
    window.__COSMETINK_LOGOUT_TIMER__ = setTimeout(() => {
      const hash = window.location.hash || "";
      if (hash.includes("/login")) return;
      handleSessionExpired("expired");
    }, msUntilExpiry);
  } else {
    // No token – regular empty store
    const hash = window.location.hash || "";
    if (!hash || hash === "#" || hash === "#/" || hash.includes("/dashboard")) {
      window.location.hash = "#/login";
    }
    store = configureStore({});
    window.__COSMETINK_STORE__ = store;
  }

  // ✅ Global 401 catcher: if ANY request returns 401, soft-logout + modal
  // (This is what fixes "still lets me take the quiz" because it catches the failing submit call.)
  if (!window.__COSMETINK_AXIOS_401_INTERCEPTOR__) {
    window.__COSMETINK_AXIOS_401_INTERCEPTOR__ = axios.interceptors.response.use(
      (res) => res,
      (err) => {
        const status = err?.response?.status;
        if (status === 401) {
          const hash = window.location.hash || "";
          if (!hash.includes("/login")) {
            handleSessionExpired("401");
          }
        }
        return Promise.reject(err);
      }
    );
  }

// Register Service Worker (required for Push)
if ("serviceWorker" in navigator) {
  navigator.serviceWorker
    .register("/sw.js")
    .catch((err) => console.warn("Service worker registration failed:", err));

  navigator.serviceWorker.addEventListener("message", (event) => {
    if (event.data?.type !== "NAVIGATE" || !event.data.url) return;

    // url is like "/#/admin/messages?thread=123"
    const targetHash = event.data.url.startsWith("/#")
      ? event.data.url.slice(1) // "/#/..." -> "#/..."
      : event.data.url;

    // Navigate SPA via hash (works in Chrome/Safari/iOS PWA)
    window.location.hash = targetHash;

    // Some setups only react on hashchange
    window.dispatchEvent(new HashChangeEvent("hashchange"));
  });
}





  const rootEl = document.getElementById("root");
  createRoot(rootEl).render(<Root store={store} />);
});
